import { strl_algoliaCardTrackingSpread } from './algolia-card-tracking';

/**
 * Search results card (matches template-parts/partials/grid-card-compact.php on search).
 *
 * @param {Record<string, unknown>} hit Algolia record.
 * @param {{ html: function, components: object }} helpers InstantSearch helpers.
 * @param {object} vars Localized strings and config from search_algolia_vars.
 */
export function strl_searchHitTemplate(hit, { html, components }, vars) {
	const categoryLvl0Attr = vars.category_lvl0_facet_attribute || 'mention_categories.lvl0';
	const postTypeSlug = strl_searchPostTypeSlug(hit, vars.post_type_labels || {});
	const isBlog = postTypeSlug === 'blog';
	const title = strl_searchHitTitle(hit);
	const url = strl_searchHitUrl(hit, vars, postTypeSlug);
	const img = strl_searchImageUrl(hit);
	const readMore = vars.read_more || '';
	const blogLabel = vars.blog_label || '';
	const pretitle = isBlog ? '' : strl_searchCategoryPretitle(hit, categoryLvl0Attr);
	const mentionSubtitleRaw = hit['mention-subtitle'];
	const hasMentionSubtitle = !isBlog && typeof mentionSubtitleRaw === 'string' && mentionSubtitleRaw.trim() !== '';
	const blogSubtitle = isBlog ? strl_searchBlogIntroduction(hit) : '';
	const hasBlogSubtitle = blogSubtitle !== '';
	const hasDateLine = !isBlog && strl_searchHasUitagendaDateLine(hit, categoryLvl0Attr, vars.locale);
	const uitagendaBlock = !isBlog ? strl_searchUitagendaDateHtml(hit, categoryLvl0Attr, vars, html) : '';
	const isInclusive = strl_searchIsInclusive(hit, categoryLvl0Attr);
	const inclusiveIconUrl = typeof vars.inclusive_icon_url === 'string' ? vars.inclusive_icon_url.trim() : '';
	const inclusiveIconAlt = typeof vars.inclusive_icon_alt === 'string' ? vars.inclusive_icon_alt : '';
	const cardTracking = strl_algoliaCardTrackingSpread(hit, {
		page_title: vars.page_title,
		template_slug: vars.template_slug,
	});

	return html`
		<article
			class="${postTypeSlug} shadow-sm group relative mb-2 flex h-full min-h-0 w-full rounded-lg md:mb-0 md:flex-col"
			...${cardTracking}
		>
			${img
				? html`<div
						class="relative aspect-[5/3] h-full w-[150px] min-w-[150px] shrink-0 md:aspect-[3/2] md:h-auto md:w-full"
					>
						<img
							src="${img}"
							alt="${title}"
							class="opacity-on-group-hover aspect-[5/3] h-full w-[150px] min-w-[150px] rounded-l-md object-cover md:aspect-[3/2] md:w-full md:rounded-t-lg md:rounded-bl-none"
							loading="lazy"
							width="320"
							height="192"
						/>
					</div>`
				: ''}
			<div
				class="color-gray-dark relative flex min-h-0 w-full min-w-0 flex-grow flex-col rounded-r-lg bg-white p-2 pr-6 text-gray-dark md:rounded-b-lg md:rounded-tr-none md:p-4 md:pr-4"
			>
				${pretitle ? html`<span class="mb-0 font-heading-small text-sm text-[#333]">${pretitle}</span>` : ''}
				${isBlog && blogLabel
					? html`<span class="mb-0 font-heading-small text-sm text-[#333]">${blogLabel}</span>`
					: ''}
				<h3
					class="${isBlog ? 'line-clamp-2' : 'line-clamp-1'} ${hasDateLine
						? ''
						: 'md:mb-2'} font-heading-small text-[16px] leading-tight md:line-clamp-3 md:text-[18px]"
				>
					${components.Highlight({ attribute: 'post_title', hit })}
				</h3>
				${uitagendaBlock}
				${hasMentionSubtitle
					? html`<span class="mb-1 line-clamp-2 text-[14px] md:mb-3 md:line-clamp-3">
							<span
								>${components.Highlight({
									attribute: 'mention-subtitle',
									hit,
								})}</span
							>
						</span>`
					: ''}
				${hasBlogSubtitle
					? html`<span class="mb-1 line-clamp-2 text-[14px] md:mb-3 md:line-clamp-3">
							<span>${blogSubtitle}</span>
						</span>`
					: ''}
				<div class="mt-auto flex w-full flex-wrap items-center gap-2">
					<span
						class="hidden w-fit items-center justify-between gap-1 rounded-full border border-secondary px-3 py-1 font-heading-small font-semibold text-secondary-dark transition-colors group-hover:bg-secondary group-hover:text-white md:inline-flex"
					>
						${readMore} <i class="fa-solid fa-caret-right"></i>
					</span>
				</div>
			</div>
			<a href="${url}" class="absolute inset-0">
				<span class="sr-only">${readMore} ${title}</span>
			</a>
			<i
				class="fa-solid fa-chevron-right absolute right-3 top-1/2 -translate-y-1/2 text-sm text-secondary transition-colors md:!hidden"
			></i>
			${isInclusive && inclusiveIconUrl
				? html`<img
						src="${inclusiveIconUrl}"
						alt="${inclusiveIconAlt}"
						class="pointer-events-none absolute bottom-2 right-2 z-10 h-8 w-8 md:bottom-4 md:right-4"
						width="32"
						height="32"
						loading="lazy"
						decoding="async"
					/>`
				: ''}
		</article>
	`;
}

/**
 * @param {Record<string, unknown>} hit
 * @param {Record<string, string>} labelsBySlug slug → localized post type name in index.
 * @returns {string}
 */
function strl_searchPostTypeSlug(hit, labelsBySlug) {
	const raw = hit.post_type;
	if (typeof raw !== 'string' || raw === '') {
		return '';
	}
	for (const [slug, label] of Object.entries(labelsBySlug)) {
		if (raw === slug || raw === label) {
			return slug;
		}
	}
	return raw;
}

/**
 * @param {Record<string, unknown>} hit
 * @returns {string}
 */
function strl_searchHitTitle(hit) {
	if (typeof hit.post_title === 'string' && hit.post_title) {
		return strl_decodeHtmlEntities(hit.post_title);
	}
	if (typeof hit.title === 'string' && hit.title) {
		return strl_decodeHtmlEntities(hit.title);
	}
	return '';
}

/**
 * @param {string} text
 * @returns {string}
 */
function strl_decodeHtmlEntities(text) {
	if (typeof text !== 'string' || text === '') {
		return text;
	}
	const el = document.createElement('textarea');
	el.innerHTML = text;
	return el.value;
}

/**
 * Network URL: origin + district + /uitje|blog/ + post_name.
 *
 * @param {Record<string, unknown>} hit
 * @param {object} vars
 * @param {string} postTypeSlug Resolved post type slug (e.g. mention, blog).
 * @returns {string}
 */
function strl_searchHitUrl(hit, vars, postTypeSlug) {
	const origin = vars.network_home_url || '';
	const subsite = typeof vars.subsite_district_slug === 'string' ? vars.subsite_district_slug : '';
	const districtSlugs = Array.isArray(hit.tax_district_slug)
		? hit.tax_district_slug.filter((s) => typeof s === 'string' && s.length > 0)
		: [];
	let district = districtSlugs[0] || '';
	if (districtSlugs.length > 1 && subsite && districtSlugs.includes(subsite)) {
		district = subsite;
	}
	const postName = typeof hit.post_name === 'string' && hit.post_name ? hit.post_name : '';
	const pathSegment = postTypeSlug === 'blog' ? 'blog' : 'uitje';

	if (origin && district && postName) {
		const base = origin.replace(/\/*$/, '/');
		return `${base}${district}/${pathSegment}/${postName}/`;
	}

	if (typeof hit.url === 'string' && hit.url.trim()) {
		return hit.url.trim();
	}

	return '#';
}

/**
 * @param {Record<string, unknown>} hit
 * @returns {string}
 */
function strl_searchImageUrl(hit) {
	const mentionImages = hit['mention-images'];
	if (Array.isArray(mentionImages) && mentionImages[0] && typeof mentionImages[0] === 'object') {
		const first = mentionImages[0];
		if (typeof first.url === 'string' && first.url) {
			return first.url;
		}
		const medium = first['strl-medium'];
		const large = first['strl-large'];
		if (medium && typeof medium === 'object' && medium.url) {
			return String(medium.url);
		}
		if (large && typeof large === 'object' && large.url) {
			return String(large.url);
		}
	}

	if (typeof hit.featured_image === 'string' && hit.featured_image) {
		return hit.featured_image;
	}

	const blogImage = hit['blog-featured-image'];
	if (blogImage && typeof blogImage === 'object' && typeof blogImage.url === 'string') {
		return blogImage.url;
	}

	return '';
}

/**
 * All lvl0 category names from {@link categoryLvl0Attr} (e.g. mention_categories.lvl0).
 *
 * @param {Record<string, unknown>} hit
 * @param {string} lvl0Attribute
 * @returns {string}
 */
function strl_searchCategoryPretitle(hit, lvl0Attribute) {
	const raw = hit[lvl0Attribute];
	if (!Array.isArray(raw) || raw.length === 0) {
		return '';
	}
	const names = raw
		.map((s) => (typeof s === 'string' ? strl_decodeHtmlEntities(s.trim()) : ''))
		.filter((n) => n !== '');
	if (names.length === 0) {
		return '';
	}
	return names.join(' | ');
}

/**
 * @param {Record<string, unknown>} hit
 * @returns {string}
 */
function strl_searchBlogIntroduction(hit) {
	const intro = hit['blog-introduction'];
	return typeof intro === 'string' ? strl_decodeHtmlEntities(intro.trim()) : '';
}

/**
 * True when {@link strl_searchUitagendaDateHtml} renders a line below the title.
 *
 * @param {Record<string, unknown>} hit
 * @param {string} lvl0Attribute
 * @param {string|undefined} locale
 * @returns {boolean}
 */
function strl_searchHasUitagendaDateLine(hit, lvl0Attribute, locale) {
	const dates = hit.dates;
	if (Array.isArray(dates) && dates.length > 0) {
		const first = dates[0];
		const startRaw = first && typeof first === 'object' && first !== null && 'start' in first ? first.start : '';

		return Boolean(strl_formatYmdShort(startRaw, locale));
	}

	return strl_searchHasUitagendaCategory(hit, lvl0Attribute);
}

/**
 * @param {Record<string, unknown>} hit
 * @param {string} lvl0Attribute
 * @returns {boolean}
 */
function strl_searchHasUitagendaCategory(hit, lvl0Attribute) {
	const raw = hit[lvl0Attribute];
	if (Array.isArray(raw)) {
		if (raw.some((s) => typeof s === 'string' && strl_decodeHtmlEntities(s.trim()) === 'Uitagenda')) {
			return true;
		}
	}

	const lvl1 = hit['mention_categories.lvl1'];
	if (Array.isArray(lvl1)) {
		if (
			lvl1.some((s) => {
				if (typeof s !== 'string') {
					return false;
				}
				const parts = strl_decodeHtmlEntities(s.trim())
					.split('>')
					.map((p) => p.trim());
				return parts.some((p) => p === 'Uitagenda');
			})
		) {
			return true;
		}
	}

	const tax = hit.tax_mention_categories;
	if (Array.isArray(tax)) {
		if (
			tax.some((t) => {
				if (!t || typeof t !== 'object') {
					return false;
				}
				const name = typeof t.name === 'string' ? strl_decodeHtmlEntities(t.name.trim()) : '';
				const slug = typeof t.slug === 'string' ? t.slug.trim() : '';
				return name === 'Uitagenda' || slug === 'uitagenda';
			})
		) {
			return true;
		}
	}

	return false;
}

/**
 * @param {Record<string, unknown>} hit
 * @param {string} lvl0Attribute
 * @param {object} vars
 * @param {function} html
 * @returns {unknown}
 */
function strl_searchUitagendaDateHtml(hit, lvl0Attribute, vars, html) {
	const dates = hit.dates;
	if (Array.isArray(dates) && dates.length > 0) {
		const first = dates[0];
		const startRaw = first && typeof first === 'object' && first !== null && 'start' in first ? first.start : '';
		const formatted = strl_formatYmdShort(startRaw, vars.locale);
		if (!formatted) {
			return '';
		}

		const more = dates.length - 1;
		const moreLabel = vars.uitagenda_more_dates_label || '';

		return html`<span class="font-semibold text-primary">
			${formatted}${more > 0 && moreLabel ? html` <span class="text-[10px]">+ ${moreLabel}</span>` : ''}
		</span>`;
	}

	if (!strl_searchHasUitagendaCategory(hit, lvl0Attribute)) {
		return '';
	}

	return html`<span class="font-semibold leading-tight text-secondary-dark"
		>${vars.uitagenda_past_label || ''}</span
	>`;
}

/**
 * @param {unknown} ymd
 * @param {string|undefined} locale
 * @returns {string}
 */
function strl_formatYmdShort(ymd, locale) {
	const s = String(ymd ?? '');
	if (!/^\d{8}$/.test(s)) {
		return '';
	}
	const year = parseInt(s.substring(0, 4), 10);
	const month = parseInt(s.substring(4, 6), 10) - 1;
	const day = parseInt(s.substring(6, 8), 10);
	const dateObj = new Date(year, month, day);
	if (Number.isNaN(dateObj.getTime())) {
		return '';
	}
	return dateObj.toLocaleDateString(locale || undefined, {
		weekday: 'short',
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});
}

/**
 * @param {Record<string, unknown>} hit
 * @param {string} lvl0Attribute
 * @returns {boolean}
 */
function strl_searchIsInclusive(hit, lvl0Attribute) {
	const raw = hit[lvl0Attribute];
	if (!Array.isArray(raw)) {
		return false;
	}
	return raw.some((s) => typeof s === 'string' && strl_decodeHtmlEntities(s.trim()) === 'Inclusief');
}
